import React from "react";

import { SESSION_UTM_PARAMETERS_KEY } from "@config/consts";

import { sessionStorageFacade } from "@services/SessionStorageFacade";

type UTMParameters =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_term"
  | "utm_content";
type UTMState = {
  [key in UTMParameters]: string | null;
};

const defaultValue: UTMState = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
};

const UTMContext = React.createContext<UTMState | null>(null);

const getUTMParamsFromURL = (): UTMState | undefined => {
  const locationHref = window.location.href;

  if (!locationHref.includes("?")) return;

  const urlParams = new URLSearchParams(locationHref.split("?")[1]);
  const utmParamsKeys = Object.keys(defaultValue) as UTMParameters[];
  const utmObject = {} as UTMState;

  utmParamsKeys.forEach(key => {
    const value = urlParams.get(key);

    if (value) {
      utmObject[key] = value;
    }
  });

  return utmObject;
};

interface UTMProviderProps {
  children: React.ReactNode;
}

const UTMProvider = (props: UTMProviderProps): React.ReactElement => {
  const sessionStorage = sessionStorageFacade();
  const params = React.useCallback((): UTMState => {
    const utmParamsFromURL = getUTMParamsFromURL();
    const sessionParams: UTMState = JSON.parse(
      sessionStorage.getItem(SESSION_UTM_PARAMETERS_KEY) ?? "{}",
    );
    const data = {
      ...defaultValue,
      ...(!!utmParamsFromURL && !!Object.keys(utmParamsFromURL).length
        ? utmParamsFromURL
        : sessionParams),
    };

    sessionStorage.setItem(SESSION_UTM_PARAMETERS_KEY, JSON.stringify(data));
    return data;
  }, [])();

  return (
    <UTMContext.Provider value={{ ...params }}>
      {props.children}
    </UTMContext.Provider>
  );
};

const useUTM = () => {
  const context = React.useContext(UTMContext);

  if (context === null) {
    throw new Error("useUTM must be used within UTMProvider");
  }

  return React.useMemo(
    () => ({
      params: { ...context },
      getNotNullableParams: () =>
        Object.fromEntries(
          Object.entries({ ...context }).filter(([_, v]) => v !== null),
        ) as {
          [k: string]: string;
        },
    }),
    [context],
  );
};

export { UTMProvider, useUTM };
